import { ArrowDown, ArrowUp, BadgeAlert, Banknote, CalendarClock, TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';
import { StatCard } from '@/components/ui/StatCard';
import { CashFlowChart } from '@/components/financial/CashFlowChart';
import { ExpenseByCategoryChart, RevenueByCategoryChart } from '@/components/financial/RevenueExpensePies';
import { AccountsBalance } from '@/components/financial/AccountsBalance';
import { UpcomingPayables, UpcomingReceivables } from '@/components/financial/PayablesReceivables';
import { DREsummary } from '@/components/financial/DREsummary';
import { HealthMetrics, OperatorTasks, TopVendors } from '@/components/financial/HealthAndTasks';
import { RecentTransactions } from '@/components/financial/RecentTransactions';
import { useAccounts, useDRESummary, useHealthMetrics, useUpcomingPayables, useUpcomingReceivables } from '@/hooks/useFinancialData';
import { formatBRL, formatBRLCompact, formatDelta } from '@/lib/format';

export function FinancialDashboard({ dateRange }: { dateRange?: { from: Date; to: Date } }) {
  const { accounts } = useAccounts();
  const { data: dre } = useDRESummary(dateRange?.from, dateRange?.to);
  const { data: health } = useHealthMetrics();
  const { data: payables } = useUpcomingPayables();
  const { data: receivables } = useUpcomingReceivables();

  const totalBalance = accounts.reduce((s, a) => s + a.balance_cents, 0);
  const totalPayables = payables.reduce((s, p) => s + p.amount_cents, 0);
  const totalReceivables = receivables.reduce((s, r) => s + r.amount_cents, 0);
  const projected30d = totalBalance + totalReceivables - totalPayables;
  const overduePayables = payables.filter((p) => p.status === 'overdue');

  const netIncome = dre?.net_income_cents ?? 0;
  const prevNetIncome = dre?.prev_net_income_cents ?? 0;
  const netDelta = formatDelta(netIncome, prevNetIncome);

  const noData = accounts.length === 0;

  return (
    <div className="space-y-6">
      {noData && (
        <div className="rounded-2xl border border-brand-200 bg-brand-50 p-5 flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-brand-900">Nenhum dado financeiro ainda</p>
            <p className="text-sm text-brand-700 mt-0.5">
              Importe o extrato da sua conta bancária em CSV para começar a visualizar seus dados.
            </p>
          </div>
          <Link to="/financeiro/importar" className="btn-primary shrink-0">
            Importar extrato CSV
          </Link>
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <StatCard
          label="Saldo consolidado"
          value={formatBRL(totalBalance)}
          hint="todas as contas"
          icon={Wallet}
        />
        <StatCard
          label="Saldo projetado (30d)"
          value={formatBRLCompact(projected30d)}
          hint="caixa + a receber − a pagar"
          icon={CalendarClock}
        />
        <StatCard
          label="A pagar (30d)"
          value={formatBRL(totalPayables)}
          trend="bad"
          delta={{ direction: overduePayables.length > 0 ? 'up' : 'flat', label: `${overduePayables.length} atrasada(s)` }}
          icon={ArrowDown}
        />
        <StatCard
          label="A receber (30d)"
          value={formatBRL(totalReceivables)}
          icon={ArrowUp}
        />
        <StatCard
          label="Resultado líquido (mês)"
          value={formatBRL(netIncome)}
          delta={netDelta}
          icon={netIncome >= prevNetIncome ? TrendingUp : TrendingDown}
        />
        <StatCard
          label="Inadimplência"
          value={formatBRL(health?.overdue_receivables_cents ?? 0)}
          trend="bad"
          hint="a receber em atraso"
          infoTooltip="Soma dos recebíveis com vencimento no passado que ainda não foram marcados como pagos. Gerencie em Financeiro → A Receber."
          icon={BadgeAlert}
        />
      </div>

      {/* Cash flow + DRE */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <CashFlowChart />
        </div>
        <DREsummary />
      </div>

      {/* Pies + accounts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <RevenueByCategoryChart />
        <ExpenseByCategoryChart />
        <AccountsBalance />
      </div>

      {/* Pay/recv + tasks + vendors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        <UpcomingPayables />
        <UpcomingReceivables />
        <OperatorTasks />
      </div>

      {/* Health + vendors */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <HealthMetrics />
        </div>
        <TopVendors />
      </div>

      {/* Transações recentes */}
      <RecentTransactions dateRange={dateRange} />

      <div className="text-xs text-ink-soft flex items-center gap-2 justify-end">
        <Banknote className="w-3 h-3" />
        Dados em tempo real via Supabase. Importe extratos em{' '}
        <Link to="/financeiro/importar" className="underline hover:text-ink">
          Importar CSV
        </Link>.
      </div>
    </div>
  );
}
